import type {
  FormikConfig,
  FormikErrors,
  FormikHelpers,
  FormikValues,
} from 'formik';
import { Formik, setIn } from 'formik';
import type { GraphQLFormattedError } from 'graphql';
import * as inflection from 'inflection';
import { merge } from 'lodash-es';
import React, { useRef } from 'react';
import type { SetOptional } from 'type-fest';
import type { ApolloError } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { miscToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import normalizeGraphQLError, {
  ErrorCodes,
} from '../utils/error/normalizeGraphQLError';

export { default as FieldSet } from './formInternals/FieldSet';
export { default as Field } from './formInternals/FormikFormField';
export { default as Submit } from './formInternals/FormikSubmitButton';

export type FormProps<Values> = SetOptional<
  FormikConfig<Values>,
  'onSubmit'
> & {
  apiErrorMessages?: { [code: string]: string };
  apiErrorHandlers?: {
    [code: string]: (
      error: GraphQLFormattedError,
      formik: FormikHelpers<Values>,
    ) => void;
  };
};

const Form = <Values extends FormikValues = FormikValues>({
  apiErrorMessages = {},
  apiErrorHandlers = {},
  validate,
  onSubmit,
  children,
  ...formikProps
}: FormProps<Values>) => {
  const dispatch = useAppDispatch();
  const formElement = useRef<HTMLFormElement | null>(null);

  const getErrorHandler = (formik: FormikHelpers<Values>) => {
    return (errors: ApolloError) => {
      if (!errors.graphQLErrors) {
        throw errors;
      }

      const { graphQLErrors } = errors;
      const { fieldErrors, unhandledErrors } = graphQLErrors.reduce<{
        fieldErrors: FormikErrors<Values>;
        unhandledErrors: GraphQLFormattedError[];
      }>(
        (memo, error) => {
          const normalizedError = normalizeGraphQLError(error);
          const { code } = normalizedError;

          if (code) {
            const errorHandler = apiErrorHandlers[code];
            if (errorHandler) {
              errorHandler(error, formik);
              return memo;
            }

            const errorMessage = apiErrorMessages[code];
            if (errorMessage) {
              dispatch(
                UIActions.pushToast(
                  miscToasts.anyError({ message: errorMessage }),
                ),
              );
              return memo;
            }
          }

          if (
            normalizedError.code === ErrorCodes.VALIDATION &&
            'field' in normalizedError &&
            typeof normalizedError.field === 'string'
          ) {
            let { message } = normalizedError;
            if (message.startsWith(`[${normalizedError.field}]`)) {
              message = message.slice(normalizedError.field.length + 2).trim();
            }
            return {
              ...memo,
              fieldErrors: {
                ...memo.fieldErrors,
                [normalizedError.field]: inflection.capitalize(message),
              },
            };
          }

          return {
            ...memo,
            unhandledErrors: [...memo.unhandledErrors, error],
          };
        },
        { fieldErrors: {}, unhandledErrors: [] },
      );

      if (Object.keys(fieldErrors).length) {
        formik.setErrors(fieldErrors);
      }

      if (unhandledErrors.length) {
        dispatch(UIActions.pushToast(miscToasts.genericError));
      }

      // TODO handle network errors
    };
  };

  const handleValidate = async (values: Values) => {
    const errors = validate?.(values);
    if (!formElement.current) {
      return errors;
    }

    // Validation function is triggered by formik state change, which might not be already reflected in the HTML input value.
    // Therefore we need to wait for it to be updated.
    await Promise.resolve();

    let browserErrors = {};
    const inputs = formElement.current.elements;

    [...inputs].forEach((input) => {
      const name =
        'name' in input && typeof input.name === 'string' ? input.name : null;
      const validationMessage =
        'validationMessage' in input ? input.validationMessage : null;
      if (name && validationMessage) {
        browserErrors = setIn(browserErrors, name, validationMessage);
      }
    });

    return merge({}, browserErrors, errors);
  };

  const handleSubmit = (values: Values, formik: FormikHelpers<Values>) => {
    const promise = onSubmit?.(values, formik);

    if (promise?.catch) {
      return promise.catch(getErrorHandler(formik)).then(() => {
        formik.setSubmitting(false);
      });
    }

    formik.setSubmitting(false);
    return promise;
  };

  return (
    <Formik<Values>
      {...formikProps}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {(renderProps) => (
        <form
          onSubmit={renderProps.handleSubmit}
          ref={(form) => {
            formElement.current = form;
          }}
          noValidate
        >
          {typeof children === 'function' ? children(renderProps) : children}
        </form>
      )}
    </Formik>
  );
};

export default Form;
